import { ITENS_CURA } from './consumiveis';
import { ehMagia, MAGIAS, type MagiaId } from './magias';
import { ehArmaCombate } from './armas';

export const COMBATE_IDLE_CAP = 3;
export const COMBATE_IDLE_CAP_CURA = 3;
/** Buffs que a IA mantém ativos em si (techspec-magias-de-efeito). O mesmo CapManter do servidor. */
export const COMBATE_IDLE_CAP_MANTER = 4;
export const MAGIA_ATRASADA: MagiaId = 'explosion';

/** HUD V3 (techspec-hud-v3 §08): só Monstros e Players. O `padrao` das versões 1 e 2 migra na leitura. */
export type CombatIdleAlvo = 'monstro' | 'jogador';

export const ALVOS_IDLE: readonly CombatIdleAlvo[] = ['monstro', 'jogador'];

export interface CombatIdleAntidoto {
  readonly usar: 'magia' | 'item';
  readonly id: string;
}

export interface CombatIdleCura {
  readonly usar: 'magia' | 'item';
  readonly id: string;
  readonly abaixoDe: number;
}

export interface RegraDeAtaque {
  readonly alvo: CombatIdleAlvo;
  readonly usar: readonly string[];
}

export interface CombatIdleConfig {
  readonly versao: number;
  readonly ataque: readonly RegraDeAtaque[];
  readonly veneno: CombatIdleAntidoto | null;
  readonly curar: readonly CombatIdleCura[];
  /** % da mana abaixo do qual a IA bebe poção. 0 = não usa. */
  readonly reservaDeMana: number;
  /** Magias de buff que a IA reconjura em si quando acabam (techspec-magias-de-efeito). */
  readonly manter: readonly string[];
}

export const COMBATE_IDLE_VERSAO = 3;

export const COMBATE_IDLE_VAZIO: CombatIdleConfig = {
  versao: COMBATE_IDLE_VERSAO,
  ataque: [{ alvo: 'monstro', usar: [] }, { alvo: 'jogador', usar: [] }],
  veneno: null,
  curar: [],
  reservaDeMana: 0,
  manter: []
};

export const ATAQUES_IDLE: readonly MagiaId[] = Object.values(MAGIAS)
  .filter(m => m.kind === 'damage' && m.id !== MAGIA_ATRASADA)
  .sort((a, b) => a.pagina - b.pagina || a.posicao - b.posicao)
  .map(m => m.id);

/** As magias de buff que a IA pode manter (as de "só em si" e as que também vão em aliado; a IA conjura em si). */
export const BUFFS_IDLE: readonly MagiaId[] = Object.values(MAGIAS)
  .filter(m => m.kind === 'buff')
  .sort((a, b) => a.pagina - b.pagina || a.posicao - b.posicao)
  .map(m => m.id);

export const CURAS_IDLE: readonly MagiaId[] = Object.values(MAGIAS)
  .filter(m => m.kind === 'heal')
  .sort((a, b) => a.pagina - b.pagina || a.posicao - b.posicao)
  .map(m => m.id);

const ATAQUES = new Set<string>(ATAQUES_IDLE);

export function ehAtaqueIdle(id: string): boolean {
  return ATAQUES.has(id) || ehArmaCombate(id);
}

export function ehAntidotoIdle(id: string): id is MagiaId {
  return ehMagia(id) && MAGIAS[id].kind === 'cure';
}

export function ehCuraDeMagiaIdle(id: string): id is MagiaId {
  return ehMagia(id) && MAGIAS[id].kind === 'heal';
}

export function ehCuraDeItemIdle(id: string): boolean {
  return (ITENS_CURA as readonly string[]).includes(id);
}

export { ITENS_CURA };

export const ANTIDOTO_PADRAO: MagiaId = 'cure';

function clampAbaixo(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.trunc(n) : 50;
  return Math.min(90, Math.max(10, v));
}

function clampReserva(n: unknown): number {
  const v = typeof n === 'number' && Number.isFinite(n) ? Math.trunc(n) : 0;
  return Math.min(90, Math.max(0, v));
}

function compactarUsar(bruto: unknown): string[] {
  const arr = Array.isArray(bruto) ? bruto : [];
  const usar: string[] = [];
  for (const v of arr) {
    if (typeof v !== 'string' || !ehAtaqueIdle(v) || usar.includes(v)) continue;
    usar.push(v);
    if (usar.length === COMBATE_IDLE_CAP) break;
  }
  return usar;
}

/**
 * Lê e limpa como o servidor (CombatIdleCatalog.Pad). Versões 1 e 2 migram: a regra `padrao` vira a de
 * Monstros quando Monstros está vazia ou não existe; senão é descartada.
 */
export function lerCombatIdle(raw: unknown): CombatIdleConfig {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const versao = obj.versao;
  if (versao !== 1 && versao !== 2 && versao !== COMBATE_IDLE_VERSAO) return COMBATE_IDLE_VAZIO;

  const regras = new Map<string, string[]>();
  const bruto = Array.isArray(obj.ataque) ? obj.ataque : [];
  const pareceV1 = versao === 1 || bruto.some(v => typeof v === 'string');
  if (pareceV1) {
    regras.set('padrao', compactarUsar(bruto));
  } else {
    for (const v of bruto) {
      if (!v || typeof v !== 'object') continue;
      const linha = v as Record<string, unknown>;
      const alvo = linha.alvo;
      if (alvo !== 'monstro' && alvo !== 'jogador' && alvo !== 'padrao') continue;
      if (regras.has(alvo)) continue;
      regras.set(alvo, compactarUsar(linha.usar));
    }
  }
  const padrao = versao === COMBATE_IDLE_VERSAO ? undefined : regras.get('padrao');
  const monstro = regras.get('monstro') ?? [];
  const ataque: RegraDeAtaque[] = [
    { alvo: 'monstro', usar: monstro.length === 0 && padrao ? padrao : monstro },
    { alvo: 'jogador', usar: regras.get('jogador') ?? [] }
  ];

  const anti = obj.veneno as Record<string, unknown> | null | undefined;
  const veneno =
    anti && anti.usar === 'magia' && typeof anti.id === 'string' && ehAntidotoIdle(anti.id)
      ? { usar: 'magia' as const, id: anti.id }
      : null;

  const curar: CombatIdleCura[] = [];
  const visto = new Set<string>();
  const brutoCura = Array.isArray(obj.curar) ? obj.curar : [];
  for (const v of brutoCura) {
    if (!v || typeof v !== 'object') continue;
    const linha = v as Record<string, unknown>;
    const usar = linha.usar === 'item' || linha.usar === 'magia' ? linha.usar : null;
    const id = typeof linha.id === 'string' ? linha.id : '';
    if (!usar) continue;
    const ok = usar === 'item' ? ehCuraDeItemIdle(id) : ehCuraDeMagiaIdle(id);
    if (!ok) continue;
    const chave = `${usar}:${id}`;
    if (visto.has(chave)) continue;
    visto.add(chave);
    curar.push({ usar, id, abaixoDe: clampAbaixo(linha.abaixoDe) });
    if (curar.length === COMBATE_IDLE_CAP_CURA) break;
  }

  return {
    versao: COMBATE_IDLE_VERSAO,
    ataque,
    veneno,
    curar,
    reservaDeMana: clampReserva(obj.reservaDeMana),
    manter: lerManter(obj.manter)
  };
}

function lerManter(bruto: unknown): string[] {
  const arr = Array.isArray(bruto) ? bruto : [];
  const out: string[] = [];
  for (const v of arr) {
    if (typeof v !== 'string' || !(BUFFS_IDLE as readonly string[]).includes(v) || out.includes(v)) continue;
    out.push(v);
    if (out.length === COMBATE_IDLE_CAP_MANTER) break;
  }
  return out;
}
